import React, { useEffect, useState } from 'react';
import { BwjValidityState } from '../../entities/bwj-validity-state';
import { where, any, none, remove } from '../../utils/collection';
import { IBwjInputProps } from '../bwj-input-base';
import { IBwjUploadFieldPrompt, IBwjUploadFieldStyle } from './entities';
import { BwjUploadFieldUi } from './ui';
import { getFileId, getFileType } from '../../utils/file';

interface IBwjUploadFieldProps extends IBwjInputProps<File[]> {
  uploadPrompt?: IBwjUploadFieldPrompt;
  style?: IBwjUploadFieldStyle;
  accept?: string[];
  disabled?: boolean;
  allowMultipleFiles?: boolean;
}

const filterAcceptTypes = (accept?: string[]) => {
  if(!accept) { return []; }

  // remove any invalid accept types
  const validTypes = where(accept, t => /^(\*\.|\.)?[a-z]+$/i.test(t));
  // normalize all accepted file types (no ., or *. before file ending)
  return validTypes.map(t => t.indexOf('.') === -1 ? t.toLowerCase() : getFileType(t));
};

export const BwjUploadField: React.FunctionComponent<IBwjUploadFieldProps> = (props) => {

  const { accept, input } = props;
  const [dragTimeout, setDragTimeout] = useState<number | undefined>();
  const [isDragging, setIsDragging] = useState(false);
  const [acceptedTypes, setAcceptedTypes] = useState<string[]>([]);

  useEffect(() => {
    setAcceptedTypes(filterAcceptTypes(accept));
  }, [accept]);

  const filterSelectedFiles = (files: File[]) => {
    return where(files, f =>
        (!acceptedTypes.length || any(acceptedTypes, t => t === getFileType(f.name))) &&
        none(input.value, val => getFileId(val) === getFileId(f))
      );
  };

  const onDragTimeout = () => {
    setIsDragging(false);
    setDragTimeout(undefined);
  };

  const onDragOver = (e:React.DragEvent<HTMLDivElement>) => {
    !isDragging && setIsDragging(true);
    
    if(!!dragTimeout) {
      window.clearTimeout(dragTimeout);
    }

    const dto = window.setTimeout(onDragTimeout, 500);
    setDragTimeout(dto);

    e.stopPropagation();
    e.preventDefault();
  }

  const onSelectFileChange = (e:React.ChangeEvent<HTMLInputElement>) => {
    if(!e.target.files) { return; }

    const newFiles: File[] = [];
    for (let i = 0; i < e.target.files.length; i++) {
      newFiles.push(e.target.files[i]);
    }

    handleSelectedFiles(newFiles);
  };

  const onDrop = (e:React.DragEvent<HTMLDivElement>) => {
    onDragTimeout();
    e.preventDefault();

    // no drag/drop if disabled or processing files
    if(!!props.disabled || input.validity === BwjValidityState.Indeterminate) {
      return;
    }

    let newFiles: File[] = [];

    if (!!e.dataTransfer.items) {
      const arrItems: DataTransferItem[] = [];
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        arrItems.push(e.dataTransfer.items[i]);
      }

      newFiles = where(arrItems, i => i.kind === 'file')
      .map(i => i.getAsFile() as File);
    } else {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        newFiles.push(e.dataTransfer.files[i]);
      }
    }

    handleSelectedFiles(newFiles);
  }

  const handleSelectedFiles = (files: File[]) => {
    let newFiles = filterSelectedFiles(files);

    if(!!newFiles.length) {
      newFiles = !!props.allowMultipleFiles ? input.value.concat(newFiles) : [newFiles[0]];
      !!props.onUpdate && props.onUpdate(newFiles);
    }
  }

  const onRemoveFile = (file: File) => {
    const files = remove(input.value, f => f === file);
    !!props.onUpdate && props.onUpdate(files);
  };

  return (
    <BwjUploadFieldUi
      id={props.id}
      required={props.required}
      label={props.label}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      input={props.input}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onSelectFileChange={onSelectFileChange}
      onRemoveFile={onRemoveFile}
      isDragging={isDragging}
      disabled={!!props.disabled || input.validity === BwjValidityState.Indeterminate}
      uploadPrompt={props.uploadPrompt}
      rootStyle={props.style?.root}
      wrapperStyle={props.style?.wrapper}
      labelStyle={props.style?.label}
      fieldStyle={props.style?.field}
      uploadIconStyle={props.style?.uploadIcon}
      dropZonePromptStyle={props.style?.dropZonePrompt}
    />
  );
};
